import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import * as React from "react";
import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from "recharts";
import {InsightsTooltipPanel, TooltipRow, TooltipTitle} from "../../kit/InsightsTooltip";
import {CHART_DRAW_DURATION, CHART_HEIGHT} from "../../kit/insightsChart";

/** One bucket: its axis label, plus this bucket's value under every series id. */
export interface SeriesPlotRow {
    key: string;
    label: string;
    full: string;
    [seriesId: string]: string | number | null;
}

export interface PlottedSeries {
    id: string;
    label: string;
    color: string;
}

/** Below this many buckets a bare line is hard to read, so the points are marked. */
const SPARSE_POINTS = 3;

interface TooltipProps {
    active?: boolean;
    payload?: Array<{dataKey?: string | number; value?: number | null; color?: string; payload?: SeriesPlotRow}>;
    format: (value: number) => string;
    labelOf: (id: string) => string;
}

function SeriesLineTooltip({active, payload, format, labelOf}: TooltipProps) {
    const rows = (payload ?? []).filter(entry => entry.value != null);

    if (!active || rows.length === 0) {
        return null;
    }

    return (
        <InsightsTooltipPanel>
            <TooltipTitle title={rows[0]?.payload?.full ?? ""}/>

            {rows.map(entry => (
                <TooltipRow
                    key={String(entry.dataKey)}
                    swatch={entry.color}
                    label={labelOf(String(entry.dataKey))}
                    value={format(Number(entry.value))}
                />
            ))}
        </InsightsTooltipPanel>
    );
}

interface Props {
    rows: SeriesPlotRow[];
    series: PlottedSeries[];
    formatValue: (value: number) => string;
    formatTick: (value: number) => string;
}

/**
 * The house line plot for a CRM card that draws several equal series at once — rates, channels.
 * One series with a comparison or a budget belongs in `InsightsSeriesChart` instead, which paints
 * its verdict; nothing here is judged, so every line keeps its own categorical colour.
 */
export function CrmSeriesLinePlot({rows, series, formatValue, formatTick}: Props) {
    const theme = useTheme();
    const labelById = new Map(series.map(entry => [entry.id, entry.label]));
    const showDots = rows.length <= SPARSE_POINTS;

    return (
        <Box sx={{width: "100%", minWidth: 0, height: CHART_HEIGHT}}>
            <ResponsiveContainer width={"100%"} height={"100%"} minWidth={0}>
                <LineChart data={rows} margin={{top: 8, right: 8, bottom: 0, left: 0}}>
                    <CartesianGrid vertical={false} stroke={theme.palette.divider} strokeOpacity={0.8}/>

                    <XAxis
                        dataKey={"label"}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={12}
                        height={34}
                        minTickGap={24}
                        tick={{fill: theme.palette.text.secondary, fontSize: 12}}
                    />

                    <YAxis
                        width={48}
                        axisLine={false}
                        tickLine={false}
                        tickMargin={8}
                        domain={[0, "auto"]}
                        tick={{fill: theme.palette.text.secondary, fontSize: 11}}
                        tickFormatter={formatTick}
                    />

                    <RechartsTooltip
                        isAnimationActive={false}
                        offset={14}
                        cursor={{stroke: theme.palette.text.primary, strokeWidth: 1, strokeOpacity: 0.25}}
                        wrapperStyle={{pointerEvents: "none", transition: "none", outline: "none", zIndex: 10}}
                        content={(
                            <SeriesLineTooltip
                                format={formatValue}
                                labelOf={id => labelById.get(id) ?? id}
                            />
                        )}
                    />

                    {series.map(entry => (
                        <Line
                            key={entry.id}
                            type={"linear"}
                            dataKey={entry.id}
                            stroke={entry.color}
                            strokeWidth={2.5}
                            strokeLinecap={"round"}
                            dot={showDots ? {r: 3, fill: entry.color, strokeWidth: 0} : false}
                            activeDot={{r: 4, fill: entry.color, stroke: theme.palette.background.paper, strokeWidth: 2}}
                            connectNulls
                            animationDuration={CHART_DRAW_DURATION}
                            animationEasing={"ease-out"}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
}

export default CrmSeriesLinePlot;
